import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import StepsSection from "@/components/StepsSection";
import { APPROACH_IMAGE } from "@/config/site";

const PRINCIPLES = [
  {
    t: "Clarity before commitment",
    d: "Every destination on this site explains what it is, who it serves, and what to do next — before you're asked for anything.",
  },
  {
    t: "Fit over breadth",
    d: "We'd rather point you to one relevant destination than seven interesting ones. If nothing fits yet, we'll say so.",
  },
  {
    t: "Transparent referrals",
    d: "We connect you with independent providers and may receive compensation from certain referrals. You'll always know where the link leads.",
  },
  {
    t: "You decide",
    d: "Pricing, eligibility, implementation, and delivery belong to the provider you choose. Our job ends where their expertise begins.",
  },
];

export default function Approach() {
  return (
    <>
      <Seo
        title="Our Approach — How DJC Global Connects Businesses"
        description="How DJC Global works: share what you want to improve, identify a relevant solution, connect with the specialist, and decide with full information."
        path="/our-approach"
      />
      <PageHero
        eyebrow="Our Approach"
        title="A connection, made with context."
        sub="DJC Global sits between businesses and a landscape of specialized providers. Our approach is simple: understand the challenge, narrow the field, and make one good introduction."
      >
        <img
          src={APPROACH_IMAGE}
          alt="Low-angle view of a modern building against the sky"
          loading="lazy"
          className="mt-10 h-[260px] w-full rounded-md border border-silver/60 object-cover sm:h-[360px]"
          data-testid="approach-hero-image"
        />
      </PageHero>

      <section className="bg-platinum pb-4 pt-8" data-testid="approach-principles">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              What we hold to
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.06}>
                <div className="h-full rounded-md border border-silver/60 bg-white p-6 shadow-sm" data-testid={`approach-principle-${i}`}>
                  <p className="font-display text-xs font-bold text-cobalt">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-3 font-display text-lg font-bold text-ink">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/70">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <StepsSection title="From interest to introduction" />

      <section className="bg-white py-14 sm:py-20" data-testid="approach-disclosure">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-md border border-silver/60 bg-platinum p-6 sm:p-8">
              <h2 className="font-display text-xl font-bold text-ink">One thing to know</h2>
              <p className="mt-3 leading-relaxed text-ink/75">
                DJC Global is a connection point, not the provider. When you follow a referral link
                or accept an introduction, you're dealing with an independent company that sets its
                own terms. DJC Global may receive compensation from certain referrals — it never
                changes what we tell you, but you should always know it.{" "}
                <Link to="/referral-disclosure" className="font-semibold text-cobalt hover:text-midnight">
                  Read the referral disclosure
                </Link>
                .
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-10 text-center">
              <Link
                to="/contact"
                data-testid="approach-contact-cta"
                className="group inline-flex items-center gap-2 rounded-md bg-cobalt px-7 py-3.5 font-display text-sm font-semibold text-white shadow-sm transition-colors hover:bg-midnight"
              >
                Start With a Conversation
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
